import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Volume2, VolumeX, Play, Pause, ArrowRight, Sparkles } from "lucide-react";

const VIDEO_SRC = "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/y5y1akk0_VIDEO-2026-04-23-10-38-30.mp4";

const HomeVideo = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted && v.paused) { v.play(); setPlaying(true); }
  };

  return (
    <section className="py-24 px-6" style={{ background: "var(--bg-soft)" }} data-testid="home-video-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Columna izquierda: Texto */}
          <div>
            <span className="section-eyebrow">Salud y Visión en vídeo</span>
            <h2 className="section-title mt-4">
              Conócenos en <span className="handwritten">55 segundos</span>
            </h2>
            <p className="font-sans-body text-[color:var(--ink-soft)] mt-5 text-lg leading-relaxed">
              Nuestro centro renovado, el equipo, las terapias visuales con inteligencia artificial y el nuevo centro de Alcobendas — todo en un vistazo.
            </p>
            <div className="mt-7 space-y-3 font-sans-body">
              <div className="flex items-start gap-3">
                <span style={{ color: "var(--sage-500)" }}>✦</span>
                <span className="text-[color:var(--ink-soft)]">Instalaciones renovadas</span>
              </div>
              <div className="flex items-start gap-3">
                <span style={{ color: "var(--sage-500)" }}>✦</span>
                <span className="text-[color:var(--ink-soft)]">Equipo profesional y cercano</span>
              </div>
              <div className="flex items-start gap-3">
                <span style={{ color: "var(--sage-500)" }}>✦</span>
                <span className="text-[color:var(--ink-soft)]">Tecnología WIVI con inteligencia artificial</span>
              </div>
              <div className="flex items-start gap-3">
                <span style={{ color: "var(--sage-500)" }}>✦</span>
                <span className="text-[color:var(--ink-soft)]">Nuevo centro abierto en Alcobendas</span>
              </div>
            </div>
            <Link to="/centros" className="btn-ghost mt-8 inline-flex" data-testid="home-video-cta">
              Ven a conocernos <ArrowRight size={16} />
            </Link>
          </div>

          {/* Columna derecha: Vídeo vertical */}
          <div className="relative mx-auto" style={{ maxWidth: "340px", width: "100%" }}>
            {/* Halo decorativo */}
            <div className="absolute -top-10 -left-10 font-display hidden md:block" style={{ color: "var(--sage-300)", fontSize: "5rem", lineHeight: 1 }}>✦</div>
            <div className="absolute -bottom-6 -right-6 font-display hidden md:block" style={{ color: "var(--sage-300)", fontSize: "3rem" }}>✧</div>

            {/* Contenedor del vídeo vertical (estilo móvil) */}
            <div
              className="relative rounded-[2rem] overflow-hidden cursor-pointer group"
              style={{
                border: "8px solid var(--sage-700)",
                boxShadow: "0 40px 80px -20px rgba(54,66,32,0.4), 0 0 0 1px var(--sage-100)",
                aspectRatio: "9/16",
                background: "var(--sage-700)",
              }}
              onClick={togglePlay}
              data-testid="home-video-wrapper"
            >
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                muted
                playsInline
                preload="auto"
                onEnded={() => setPlaying(false)}
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                data-testid="home-video-player"
              >
                <source src={VIDEO_SRC} type="video/mp4" />
                Tu navegador no soporta vídeo HTML5.
              </video>

              {/* Overlay de play inicial */}
              {!playing && (
                <div
                  className="absolute inset-0 flex items-center justify-center transition-opacity"
                  style={{ background: "linear-gradient(180deg, rgba(54,66,32,0.15) 0%, rgba(54,66,32,0.55) 100%)" }}
                >
                  <div
                    className="h-16 w-16 md:h-20 md:w-20 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                    style={{ background: "rgba(250,247,238,0.95)", boxShadow: "0 10px 30px rgba(0,0,0,0.3)" }}
                  >
                    <Play size={26} style={{ color: "var(--sage-700)", marginLeft: "3px" }} fill="currentColor" />
                  </div>
                </div>
              )}

              {/* Badge superior */}
              <div
                className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans-body text-[10px] font-semibold backdrop-blur-md"
                style={{ background: "rgba(250,247,238,0.92)", color: "var(--sage-700)" }}
              >
                <Sparkles size={10} /> Reel
              </div>

              {/* Controles bottom-right */}
              <div className="absolute bottom-3 right-3 flex gap-1.5">
                <button
                  onClick={toggleMute}
                  aria-label={muted ? "Activar sonido" : "Silenciar"}
                  className="h-10 w-10 rounded-full flex items-center justify-center backdrop-blur-md transition-transform hover:scale-110"
                  style={{ background: "rgba(250,247,238,0.95)", color: "var(--sage-700)", boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }}
                  data-testid="home-video-mute"
                >
                  {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
                {playing && (
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                    aria-label="Pausar"
                    className="h-10 w-10 rounded-full flex items-center justify-center backdrop-blur-md transition-transform hover:scale-110"
                    style={{ background: "rgba(250,247,238,0.95)", color: "var(--sage-700)", boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }}
                    data-testid="home-video-pause"
                  >
                    <Pause size={16} />
                  </button>
                )}
              </div>

              {/* Indicador de mute cuando está reproduciendo */}
              {playing && muted && (
                <div
                  className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans-body text-[10px] backdrop-blur-md cursor-pointer"
                  style={{ background: "rgba(250,247,238,0.92)", color: "var(--sage-700)" }}
                  onClick={toggleMute}
                >
                  <VolumeX size={10} /> Toca para el sonido
                </div>
              )}
            </div>

            {/* Label bajo el "móvil" */}
            <div className="text-center mt-6 font-sans-body text-xs uppercase tracking-widest" style={{ color: "var(--sage-600)" }}>
              ✦ Tour por el centro ✦
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeVideo;
